import { getBackendUrl, getImageUrl } from "@Utils";
import { useTranslation, untranslated } from "@i18n";
import { FC, useState, useEffect, useRef } from "react";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import { useSelector } from "react-redux";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import { RootState } from "@Reducers/index";
import { SOMETHING_WENT_WRONG, TOAST_TYPE_ERROR, TOAST_TYPE_SUCCESS } from "@Services/constants";
import axiosWrapper from "@Services/axiosWrapper";
import type { UpdatePortalDetailsRequest, UpdatePortalDetailsResponse } from "@ServerTypes/portal/portal.schema";
import { showToastNotification } from "@Reducers/toastSlice";
import IconComponent from "@CubeComponents/icons";
import TextFieldComponent from "@CubeComponents/Mui_Form/TextFieldComponent";
import { FormValidator } from "@CubeComponents/Mui_Form/FormValidator";
import { convertDate } from "@Services/luxonHelper";
import { getPortalData } from "@Actions/portalSetting";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import ImageEditorModal from "@Components/UserProfileComponents/ImageEditorModal";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import Text from "@CubeComponents/html/Text/Text";
import { Box } from "@mui/material";

const PortalInformation: FC = () => {
  const { t } = useTranslation();
  const layout = useSelector((state: RootState) => state.layout);
  const portalData = useSelector((state: RootState) => state.portalData);
  const portalSettingInfo = useSelector((state: RootState) => state.portalSettingInfo);

  const [tempPortalData, setTempPortalData] = useState(portalData);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [showEditProfileModal, setShowEditProfileModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState("");
  const dispatch = useDispatch();
  const formObject = useRef<FormValidator | null>(null);

  useEffect(() => {
    setTempPortalData(portalData);
  }, [portalData]);

  useEffect(() => {
    document.title = `Portal Settings | Portal Information`;
  }, []);

  const handleEditMode = () => {
    setEditMode(!editMode);
  };

  const handleShowEditProfileModal = (key: string) => {
    setShowEditProfileModal(!showEditProfileModal);
    setSelectedImage(key);
  };

  const handleChange = (args: React.ChangeEvent<HTMLInputElement>) => {
    setTempPortalData({
      ...tempPortalData,
      [args.target.name]: args.target.value,
    });
    formObject.current!.validate();
  };

  const handleDeleteConfirmationModal = (key: string) => {
    setShowDeleteConfirmation(!showDeleteConfirmation);
    setSelectedImage(key);
  };

  const handleSave = async () => {
    if (formObject.current!.validate()) {
      // Only the changed fields are sent; the field set is the server's request schema.
      const params: UpdatePortalDetailsRequest = {};
      if (tempPortalData.name != portalData.name) {
        params.name = tempPortalData.name;
      }
      if (tempPortalData.description != portalData.description) {
        params.description = tempPortalData.description;
      }
      if (tempPortalData.logo != portalData.logo) {
        params.logo = tempPortalData.logo;
      }
      if (tempPortalData.signUpLogo != portalData.signUpLogo) {
        // `portalData.signUpLogo` is `unknown` on the redux slice type; the cast is erased.
        params.signUpLogo = tempPortalData.signUpLogo as string | undefined;
      }
      const response = await axiosWrapper.post<UpdatePortalDetailsResponse>(
        `${getBackendUrl()}/portal/updatePortalDetails`,
        params,
      );
      if (response.data.status) {
        dispatch(
          showToastNotification({
            content: t("Details updated successfully."),
            type: TOAST_TYPE_SUCCESS,
          }),
        );
      } else {
        dispatch(
          showToastNotification({
            content: untranslated(response.data.errorMessage ?? SOMETHING_WENT_WRONG),
            type: TOAST_TYPE_ERROR,
          }),
        );
      }

      dispatch(getPortalData());
      setEditMode(false);
    }
  };

  function handleExistingImage() {
    if (selectedImage == "logo") {
      return tempPortalData.logo ? tempPortalData.logoUrl : undefined;
    } else {
      return tempPortalData.signUpLogo ? tempPortalData.signUpLogoUrl : undefined;
    }
  }

  const handleCancel = () => {
    setEditMode(false);
    setTempPortalData(portalData);
  };

  useEffect(() => {
    const options = {
      rules: {
        name: {
          required: [true, "* Name is required"],
        },
        description: {
          required: [true, "* Description is required"],
        },
      },
    };
    formObject.current = new FormValidator("#portalInformationRoot", options);
  }, []);

  const handleLogoChange = (data: string) => {
    // Both keys of each pair are set together: the persisted key and the preview URL.
    const updates: { logo?: string; logoUrl?: string; signUpLogo?: string; signUpLogoUrl?: string } = {};
    if (selectedImage === "logo") {
      updates.logo = data;
      updates.logoUrl = data;
    } else if (selectedImage === "signup") {
      updates.signUpLogo = data;
      updates.signUpLogoUrl = data;
    }
    setTempPortalData({
      ...tempPortalData,
      ...updates,
    });
  };

  const handleDelete = () => {
    handleLogoChange("");
    handleDeleteConfirmationModal("");
  };

  return (
    <>
      <div id="portalInformationRoot" className={`flex flex-col m-16 w-full gap-10`}>
        <div className={"flex gap-10 items-center"} data-testid="headingContentWrapper">
          <div data-testid="heading">
            <h2 className={`text-2xl font-bold`} data-testid="portalInformationHeadingLabel">
              <Text>{t("Portal Information")}</Text>
            </h2>
          </div>
          {!editMode && portalSettingInfo.portalInformation?.edit?.enable && (
            <div data-testid="portalHeadingIcon">
              <IconComponent
                name="edit"
                className="cursor-pointer"
                onClick={handleEditMode}
                toolTipContent={t("Edit Info")}
                position={"bottom"}
                width="1.5rem"
                height="1.5rem"
              />
            </div>
          )}
        </div>

        <div className="flex flex-col gap-10 h-full">
          <div className="grid gap-4 grid-cols-2">
            <div className="flex gap-4 items-center">
              <Text className="flex-1" data-testid="portallogoText">
                {t("Portal Logo")}
              </Text>
              <div className="flex-2">
                <div
                  data-testid="portalLogo"
                  className="group rounded-2xl inline-flex p-2 relative"
                  style={{ boxShadow: "0 0 0.2625rem 1px rgba(0, 0, 0, 0.15)" }}
                >
                  <div className="flex items-center justify-center w-[4.6875rem] h-[4.6875rem] rounded-full text-white text-xl font-bold overflow-hidden">
                    <img
                      alt="Logo"
                      className="w-full h-full object-cover"
                      src={
                        tempPortalData.logo
                          ? tempPortalData.logoUrl
                          : (getImageUrl() ?? "") + (layout.HEADER_LOGO ?? "")
                      }
                    />
                  </div>

                  {editMode && layout.ALLOW_PORTAL_LOGO_UPDATE && (
                    <>
                      <Box
                        className="opacity-0 group-hover:opacity-80 hover:!opacity-50 bg-white z-10 cursor-pointer absolute flex start-0 w-1/2 h-full top-0 items-center justify-around ps-2"
                        onClick={() => handleShowEditProfileModal("logo")}
                      >
                        <IconComponent
                          name="editProfile"
                          toolTipContent={tempPortalData.logo ? t("Edit Portal Logo") : t("Add Portal Logo")}
                          position={"bottom"}
                          width="1rem"
                          height="1rem"
                        />
                      </Box>
                      {tempPortalData.logo && (
                        <Box
                          className="opacity-0 group-hover:opacity-80 hover:!opacity-50 bg-white z-10 cursor-pointer absolute flex end-0 w-1/2 h-full top-0 items-center justify-around pe-2"
                          onClick={() => {
                            if (tempPortalData.logo) {
                              handleDeleteConfirmationModal("logo");
                            }
                          }}
                        >
                          <IconComponent
                            name="delete"
                            toolTipContent={t("Remove Logo")}
                            position={"bottom"}
                            width="1rem"
                            height="1rem"
                          />
                        </Box>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
            <div className="flex gap-4 items-center">
              <Text className="flex-1" data-testid="signUpImageText">
                {t("Sign Up Image")}
              </Text>
              <div className="flex-2">
                <div
                  data-testid="signUpImage"
                  className="group rounded-2xl inline-flex p-2 relative"
                  style={{ boxShadow: "0 0 0.2625rem 1px rgba(0, 0, 0, 0.15)" }}
                >
                  <div className="flex items-center justify-center w-[6.25rem] h-[6.25rem] bg-[var(--system-colour-2)] text-white text-xl font-bold overflow-hidden">
                    {tempPortalData.signUpLogo ? (
                      <img src={tempPortalData.signUpLogoUrl} className="w-full h-full" alt="" />
                    ) : (
                      <img src={`${getImageUrl()}/SignInPageImage.webp`} className="w-full h-full" alt="" />
                    )}
                  </div>

                  {editMode && layout.ALLOW_PORTAL_LOGO_UPDATE && (
                    <>
                      <Box
                        className="opacity-0 group-hover:opacity-80 hover:!opacity-50 bg-white z-10 cursor-pointer absolute flex start-0 w-1/2 h-full top-0 items-center justify-around ps-2"
                        onClick={() => handleShowEditProfileModal("signup")}
                      >
                        <IconComponent
                          name="editProfile"
                          toolTipContent={t("Edit Sign-Up Image")}
                          position={"bottom"}
                          width="1rem"
                          height="1rem"
                        />
                      </Box>
                      {tempPortalData.signUpLogo && (
                        <Box
                          className="opacity-0 group-hover:opacity-80 hover:!opacity-50 bg-white z-10 cursor-pointer absolute flex end-0 w-1/2 h-full top-0 items-center justify-around pe-2"
                          onClick={() => {
                            if (tempPortalData.signUpLogo) {
                              handleDeleteConfirmationModal("signup");
                            }
                          }}
                        >
                          <IconComponent
                            name="delete"
                            toolTipContent={t("Remove User selected Image")}
                            position={"bottom"}
                            width="1rem"
                            height="1rem"
                          />
                        </Box>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="grid gap-4 grid-cols-2">
            <div className="flex gap-4 items-center">
              <Text className="flex-1" data-testid="portalName">
                {t("Portal Name*")}
              </Text>
              {!editMode ? (
                <span className="text-text_light flex-2" data-testid="portalNameValue">
                  {tempPortalData.name}
                </span>
              ) : (
                <div className="flex-2">
                  <TextFieldComponent
                    placeholder={t("Name")}
                    name={"name"}
                    value={tempPortalData.name}
                    onChange={handleChange}
                    slotProps={{
                      htmlInput: {
                        "data-testid": "portalNameInputField",
                      },
                    }}
                  />
                </div>
              )}
            </div>
            <div className="flex gap-4 items-center">
              <Text className="flex-1" data-testid="createdOn">
                {t("Created On")}
              </Text>
              <span className="text-text_light flex-2" data-testid="createdOnValue">
                {convertDate(Number(portalData.createdDate), "dateForText")}
              </span>
            </div>
          </div>
          <div className="grid gap-4 grid-cols-2">
            <div className="flex gap-4 items-center">
              <Text className="flex-1" data-testid="portalDesc">
                {t("Description*")}
              </Text>
              {!editMode ? (
                <span className="text-text_light flex-2" data-testid="portalDescValue">
                  {tempPortalData.description}
                </span>
              ) : (
                <div className="flex-2">
                  <TextFieldComponent
                    placeholder={t("Description")}
                    name={"description"}
                    value={tempPortalData.description}
                    onChange={handleChange}
                    slotProps={{
                      htmlInput: {
                        "data-testid": "portalDescInputField",
                      },
                    }}
                  />
                </div>
              )}
            </div>
            <div className="flex gap-4 items-center">
              <Text className="flex-1" data-testid="portalDomain">
                {t("Domain")}
              </Text>
              <span className="text-text_light flex-2" data-testid="portalDomainValue">
                {portalData.frontendUrl}
              </span>
            </div>
          </div>
        </div>

        {editMode && (
          <div className="flex gap-10">
            <ButtonComponent buttonType={BUTTON_TYPES.BORDER_BUTTON} onClick={handleCancel}>
              <Text data-testid="cancelPortalDetails">{t("Cancel")}</Text>
            </ButtonComponent>
            <ButtonComponent
              disabled={!tempPortalData.description?.trim() || !tempPortalData.name?.trim()}
              type="submit"
              buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
              onClick={handleSave}
            >
              <Text data-testid="savePortalDetails">{t("Save Changes")}</Text>
            </ButtonComponent>
          </div>
        )}
      </div>
      {showEditProfileModal && (
        <ImageEditorModal
          // @ts-expect-error -- `closeModal` is declared `() => void` but this handler takes `(key: string)`. REAL BUG: the modal calls it with no argument, so `key` is always undefined and the handler cannot branch on it.
          closeModal={handleShowEditProfileModal}
          onSubmitFunction={handleLogoChange}
          heading={
            selectedImage == "logo"
              ? tempPortalData.logo
                ? t("Edit Portal Logo")
                : t("Add Portal Logo")
              : t("Edit Sign Up Image")
          }
          existingImg={handleExistingImage()}
        />
      )}
      {showDeleteConfirmation && (
        <div>
          <ModalStateDialogComponent
            onCancel={() => handleDeleteConfirmationModal("")}
            headerTitle={
              <>
                <span>{selectedImage === "logo" ? t("Remove Portal logo?") : t("Remove current selected Image?")}</span>
              </>
            }
            footerContent={
              <div className="flex gap-4">
                <ButtonComponent
                  buttonType={BUTTON_TYPES.BORDER_BUTTON}
                  onClick={() => handleDeleteConfirmationModal("")}
                >
                  <Text>{t("Cancel")}</Text>
                </ButtonComponent>
                <ButtonComponent buttonType={BUTTON_TYPES.GRADIENT_BUTTON} onClick={handleDelete}>
                  <Text>{t("Confirm")}</Text>
                </ButtonComponent>
              </div>
            }
            modelContent={
              <div className="">
                <span>
                  {selectedImage === "logo"
                    ? t("Are you sure you want to remove the portal logo?")
                    : t("Are you sure you want to remove the current selected Image")}
                </span>
              </div>
            }
            isVisible={true}
            width="28.125rem"
          />
        </div>
      )}
    </>
  );
};

export default PortalInformation;
